export type AssistantTaskFailureClass =
  | 'execution-context'
  | 'policy-contract'
  | 'domain-readiness'
  | 'evidence-coherence'
  | 'security'
  | 'lineage';

export type AssistantTaskFailureClassification = {
  failureClass: AssistantTaskFailureClass;
  reasonCode: string;
  statusMessage: string;
  failureDetail: string;
};

const normalizeText = (value: unknown): string => String(value ?? '').trim();

const joinEvidence = (values: unknown[]): string => (
  values
    .map((value) => normalizeText(value))
    .filter(Boolean)
    .join('\n')
);

const buildExecutionContextFailure = (input: {
  reasonCode: string;
  statusMessage: string;
  failureDetail: string;
}): AssistantTaskFailureClassification => ({
  failureClass: 'execution-context',
  reasonCode: input.reasonCode,
  statusMessage: input.statusMessage,
  failureDetail: input.failureDetail,
});

export const isAssistantConversationPersistenceConfigError = (
  ...values: unknown[]
): boolean => {
  const detail = joinEvidence(values);
  if (!detail) {
    return false;
  }
  return /ASSISTANT_(?:SESSION|MESSAGE)_TABLE is required for assistant platform persistence/i.test(detail)
    || /required for assistant platform persistence/i.test(detail);
};

export const classifyAssistantTaskFailure = (
  ...values: unknown[]
): AssistantTaskFailureClassification | null => {
  const detail = joinEvidence(values);
  if (!detail) {
    return null;
  }

  if (
    /HRM runtime policy .* requires protected S3/i.test(detail)
    || /Configure HRM_RUNTIME_POLICY_S3_(?:BUCKET|KEY|REGION)/i.test(detail)
    || /assistantPlatformContractApi:\s*delegated invoke failed for mutateAssistantRun/i.test(detail)
  ) {
    return buildExecutionContextFailure({
      reasonCode: 'execution-context.hosted-workflow-runtime-policy-protected-s3-drift',
      statusMessage: 'Background task runtime is temporarily unavailable right now.',
      failureDetail: detail,
    });
  }

  if (
    /ASSISTANT_ARTIFACTS_BUCKET is not configured/i.test(detail)
    || /Assistant artifact storage is not configured for background artifact tasks/i.test(detail)
  ) {
    return buildExecutionContextFailure({
      reasonCode: 'execution-context.assistant-artifact-storage-config-missing',
      statusMessage: 'Artifact storage is temporarily unavailable right now.',
      failureDetail: detail,
    });
  }

  if (isAssistantConversationPersistenceConfigError(detail)) {
    return buildExecutionContextFailure({
      reasonCode: 'execution-context.assistant-conversation-persistence-config-missing',
      statusMessage: 'Assistant session persistence is temporarily unavailable right now.',
      failureDetail: detail,
    });
  }

  if (/ASSISTANT_RUN_STREAMER_FUNCTION_NAME is not configured/i.test(detail)) {
    return buildExecutionContextFailure({
      reasonCode: 'execution-context.assistant-run-streamer-config-missing',
      statusMessage: 'Background task execution is temporarily unavailable right now.',
      failureDetail: detail,
    });
  }

  if (
    /INTELLIO_TASK_TRANSFORM_MODEL_(?:ID|KEY)/i.test(detail)
    || /Bedrock model identifier/i.test(detail)
    || /model identifier|model.+not found|unknown model|does not exist/i.test(detail)
  ) {
    return buildExecutionContextFailure({
      reasonCode: 'execution-context.task-transform-model-config-invalid',
      statusMessage: 'Background artifact generation is temporarily unavailable right now.',
      failureDetail: detail,
    });
  }

  if (
    /forbidden|unauthorized|not authorized|token expired|origin mismatch|auth state/i.test(detail)
  ) {
    return buildExecutionContextFailure({
      reasonCode: 'execution-context.assistant-task-auth-drift',
      statusMessage: 'Background task execution is unavailable until authentication is refreshed.',
      failureDetail: detail,
    });
  }

  return null;
};
